import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, User, ArrowLeft, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const Header = ({ showBackButton = false, backTo = '/', backLabel = 'Retour', showAnalyzeCta = false }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Error parsing user:', e);
      }
    }
  }, []);

  // Hide header on scroll down, show on scroll up
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;

    if (currentScrollY < 10) {
      setIsVisible(true);
    } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
      setIsVisible(false);
    } else if (currentScrollY < lastScrollY) {
      setIsVisible(true);
    }

    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.success('Déconnexion réussie');
    navigate('/');
  };

  return (
    <header
      className={`bg-white border-b border-gray-200 sticky top-0 z-50 transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-3">
        <div className="flex items-center justify-between">
          <div
            className="flex items-center cursor-pointer"
            onClick={() => navigate('/')}
          >
            <img
              src="/logo.png"
              alt="Anti-Recouvrement.com"
              className="h-12 sm:h-16 md:h-20 w-auto"
            />
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            {showBackButton && (
              <Button
                data-testid="back-button"
                variant="ghost"
                size="sm"
                onClick={() => navigate(backTo)}
                className="text-xs sm:text-sm"
              >
                <ArrowLeft className="mr-1 sm:mr-2" size={16} />
                <span className="hidden sm:inline">{backLabel}</span>
              </Button>
            )}
            <Link
              to="/blog"
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors hover:bg-gray-100"
              style={{ color: '#1e3a8a' }}
            >
              <BookOpen size={16} className="sm:w-[18px] sm:h-[18px]" />
              <span className="hidden sm:inline">Blog</span>
            </Link>
            <Link
              to="/questions-frequentes"
              className="hidden md:flex items-center px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors hover:bg-gray-100"
              style={{ color: '#1e3a8a' }}
            >
              Questions fréquentes
            </Link>
            <Link
              to="/a-propos"
              className="hidden md:flex items-center px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors hover:bg-gray-100"
              style={{ color: '#1e3a8a' }}
            >
              À propos
            </Link>
            {showAnalyzeCta && (
              <Link
                to="/"
                className="hidden sm:inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold rounded-lg transition-transform hover:scale-105"
                style={{ backgroundColor: '#1e3a8a', color: 'white' }}
              >
                Analyser ma dette
              </Link>
            )}
            {user ? (
              <>
                <div className="hidden md:flex items-center space-x-2">
                  <User size={18} color="#1e3a8a" />
                  <span className="text-sm font-medium" style={{ color: '#1e3a8a' }}>
                    {user.nom || user.email}
                  </span>
                </div>
                <Button
                  data-testid="dashboard-button-header"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/dashboard')}
                  style={{ borderColor: '#1e3a8a', color: '#1e3a8a' }}
                  className="text-xs sm:text-sm px-2 sm:px-4"
                >
                  <span className="hidden sm:inline">Tableau de bord</span>
                  <span className="sm:hidden">Dossiers</span>
                </Button>
                <Button
                  data-testid="logout-button-header"
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  style={{ borderColor: '#dc2626', color: '#dc2626' }}
                  className="text-xs sm:text-sm px-2 sm:px-4"
                >
                  <LogOut size={14} className="sm:mr-2" />
                  <span className="hidden sm:inline">Déconnexion</span>
                </Button>
              </>
            ) : (
              <Button
                data-testid="login-button-header"
                variant="outline"
                size="sm"
                onClick={() => navigate('/login')}
                style={{ borderColor: '#1e3a8a', color: '#1e3a8a' }}
                className="text-xs sm:text-sm px-2 sm:px-4"
              >
                Connexion
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
